
import React, { useMemo } from 'react';
import { IkigaiBuckets } from '../types';
import { Heart, Globe, Briefcase, Zap, Radar } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface Props {
  data: IkigaiBuckets;
  className?: string;
  variant?: 'card' | 'floating'; // New prop
}

export const AlignmentCompass: React.FC<Props> = ({ data, className = '', variant = 'card' }) => {
  const { theme, getKaiTheme } = useTheme();
  
  // --- CONFIGURATION ---
  const SIZE = 250;           
  const CENTER = SIZE / 2;    
  
  // RADIUS CONFIG
  const RIM_RADIUS = 70;      // Scaled down for 250px size
  
  const MAX_ITEMS = 5;

  const stats = useMemo(() => {
    // 1. Normalize Scores (0 to 1.0) - Removed the 0.15 floor so it starts at true zero center
    const getScore = (arr: string[] | undefined) => Math.min(1, (arr?.length || 0) / MAX_ITEMS);
    
    const s = {
        p: getScore(data?.passion),    // Top Left
        m: getScore(data?.mission),    // Top Right
        v: getScore(data?.vocation),   // Bottom Right
        pr: getScore(data?.profession) // Bottom Left
    };

    // 2. Calculate Polygon Vertex Coordinates (DIAGONAL LAYOUT)
    const DIAG = 0.7071;

    // Passion (Top Left)
    const pX = CENTER - (s.p * RIM_RADIUS * DIAG);
    const pY = CENTER - (s.p * RIM_RADIUS * DIAG);
    
    // Mission (Top Right)
    const mX = CENTER + (s.m * RIM_RADIUS * DIAG);
    const mY = CENTER - (s.m * RIM_RADIUS * DIAG);

    // Vocation (Bottom Right)
    const vX = CENTER + (s.v * RIM_RADIUS * DIAG);
    const vY = CENTER + (s.v * RIM_RADIUS * DIAG);

    // Profession (Bottom Left)
    const prX = CENTER - (s.pr * RIM_RADIUS * DIAG);
    const prY = CENTER + (s.pr * RIM_RADIUS * DIAG);

    // Order: P -> M -> V -> Pr
    const points = `${pX},${pY} ${mX},${mY} ${vX},${vY} ${prX},${prY}`;

    // 3. Archetype Logic
    const total = s.p + s.m + s.v + s.pr;
    let label = "Calibrating";
    let subLabel = "Syncing...";
    let activeColor = "indigo";

    if (total > 3.0) {
        label = "Ikigai Aligned";
        subLabel = "Resonance Achieved";
        activeColor = "indigo";
    } else if (s.p > 0.6 && s.m > 0.6) {
        label = "The Dreamer";
        subLabel = "Purpose > Security";
        activeColor = "rose";
    } else if (s.v > 0.6 && s.pr > 0.6) {
        label = "The Provider";
        subLabel = "Security > Joy";
        activeColor = "emerald";
    } else if (s.pr > 0.6 && s.p > 0.6) {
        label = "The Hustler";
        subLabel = "High Energy";
        activeColor = "amber";
    } else if (total > 0.8) {
        label = "The Seeker";
        subLabel = "Pattern Emerging";
        activeColor = "blue";
    }

    return { s, points, label, subLabel, activeColor, pX, pY, mX, mY, vX, vY, prX, prY };
  }, [data]);

  // Get dynamic colors
  const activeTheme = getKaiTheme(stats.activeColor);

  // Helper to convert internal coords to percentage for responsive positioning
  const toPct = (val: number) => `${(val / SIZE) * 100}%`;

  // Diagonal anchors for icons (constant positions)
  const DIAG = 0.7071;
  const ANCHOR_OFFSET = RIM_RADIUS * DIAG; 

  const isFloating = variant === 'floating';

  // Container Classes
  const containerClasses = isFloating 
    ? `relative flex flex-col items-center justify-center w-full h-full pointer-events-none` 
    : `relative flex flex-col items-center justify-between h-full p-4 rounded-[40px] overflow-hidden transition-all duration-500 border backdrop-blur-xl shadow-2xl group ${theme.mode === 'dark' ? 'bg-slate-900/80 border-slate-700' : 'bg-white/80 border-white/60'}`;

  return (
    <div className={`${containerClasses} ${className}`}>
      
      {/* --- HEADER (Only for Card Mode) --- */}
      {!isFloating && (
        <div className="w-full flex items-center justify-between z-20 mb-[-20px] px-4 pt-2">
            <div className={`flex items-center gap-2 ${theme.colors.text.secondary} opacity-80`}>
                <Radar size={14} className="animate-spin-slow" />
                <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Flux Radar</span>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/5 border border-transparent dark:border-white/10">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_5px_rgba(34,197,94,0.5)]"></div>
                <span className="text-[9px] font-mono opacity-60">ONLINE</span>
            </div>
        </div>
      )}

      {/* --- THE RADAR ENGINE --- */}
      <div className={`relative w-full flex items-center justify-center ${isFloating ? '' : 'flex-1 min-h-[280px]'}`}>
         
         {/* 1. The SVG Chart Container - RESPONSIVE */}
         <div className={`relative w-full aspect-square ${isFloating ? 'max-w-[250px]' : 'max-w-[300px]'}`}>
            <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full h-full overflow-visible">
                <defs>
                    <radialGradient id="radarGradient" cx="50%" cy="50%" r="60%">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" className={activeTheme.text} />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0.0" className={activeTheme.text} />
                    </radialGradient>
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <linearGradient id="scanGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0.2" />
                    </linearGradient>
                </defs>

                {/* Grid Rings */}
                <g className={`${isFloating ? (theme.mode === 'dark' ? 'text-white/20' : 'text-slate-900/10') : 'text-slate-300 dark:text-slate-700'} transition-colors`}>
                    {/* Outer Rim */}
                    <circle cx={CENTER} cy={CENTER} r={RIM_RADIUS} fill="none" stroke="currentColor" strokeWidth={isFloating ? 2 : 1} />
                    <circle cx={CENTER} cy={CENTER} r={RIM_RADIUS + 4} fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" opacity="0.3" />
                    
                    {/* Inner Steps */}
                    <circle cx={CENTER} cy={CENTER} r={RIM_RADIUS * 0.66} fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2" opacity="0.3" />
                    <circle cx={CENTER} cy={CENTER} r={RIM_RADIUS * 0.33} fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2" opacity="0.2" />
                    
                    {/* Diagonal Axis Lines (X Shape) */}
                    <line 
                        x1={CENTER - ANCHOR_OFFSET - 20} y1={CENTER - ANCHOR_OFFSET - 20} 
                        x2={CENTER + ANCHOR_OFFSET + 20} y2={CENTER + ANCHOR_OFFSET + 20} 
                        stroke="currentColor" strokeWidth="1" opacity="0.2" 
                    />
                    <line 
                        x1={CENTER + ANCHOR_OFFSET + 20} y1={CENTER - ANCHOR_OFFSET - 20} 
                        x2={CENTER - ANCHOR_OFFSET - 20} y2={CENTER + ANCHOR_OFFSET + 20} 
                        stroke="currentColor" strokeWidth="1" opacity="0.2" 
                    />
                </g>

                {/* Radar Sweep Animation */}
                <g 
                    className="animate-[spin_4s_linear_infinite] text-indigo-500 dark:text-indigo-400"
                    style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}
                >
                    <path d={`M ${CENTER} ${CENTER} L ${CENTER} ${CENTER - RIM_RADIUS - 20} A ${RIM_RADIUS+20} ${RIM_RADIUS+20} 0 0 1 ${CENTER + RIM_RADIUS + 20} ${CENTER} Z`} fill="url(#scanGradient)" opacity="0.15" />
                    <line x1={CENTER} y1={CENTER} x2={CENTER} y2={CENTER - RIM_RADIUS - 20} stroke="currentColor" strokeWidth="1" opacity="0.5" />
                </g>

                {/* Tether Lines - Only show if bucket has at least 1 item (>0 score) */}
                <g className="opacity-40" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2">
                    <line x1={CENTER} y1={CENTER} x2={stats.pX} y2={stats.pY} className={stats.s.p > 0 ? 'text-rose-400' : 'hidden'} />
                    <line x1={CENTER} y1={CENTER} x2={stats.mX} y2={stats.mY} className={stats.s.m > 0 ? 'text-purple-400' : 'hidden'} />
                    <line x1={CENTER} y1={CENTER} x2={stats.vX} y2={stats.vY} className={stats.s.v > 0 ? 'text-emerald-400' : 'hidden'} />
                    <line x1={CENTER} y1={CENTER} x2={stats.prX} y2={stats.prY} className={stats.s.pr > 0 ? 'text-sky-400' : 'hidden'} />
                </g>

                {/* The "Soul" Polygon */}
                <polygon 
                    points={stats.points} 
                    fill="url(#radarGradient)" 
                    stroke="currentColor" 
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                    filter="url(#glow)"
                    className={`transition-all duration-1000 ease-spring ${activeTheme.text} drop-shadow-lg`}
                />

                {/* Active Vertices (Pulse) - Only show if bucket has at least 1 item (>0 score) */}
                <g className="transition-all duration-1000 ease-spring">
                    {stats.s.p > 0 && <circle cx={stats.pX} cy={stats.pY} r="3" className="fill-rose-500 animate-pulse" filter="url(#glow)" />}
                    {stats.s.m > 0 && <circle cx={stats.mX} cy={stats.mY} r="3" className="fill-purple-500 animate-pulse" filter="url(#glow)" />}
                    {stats.s.v > 0 && <circle cx={stats.vX} cy={stats.vY} r="3" className="fill-emerald-500 animate-pulse" filter="url(#glow)" />}
                    {stats.s.pr > 0 && <circle cx={stats.prX} cy={stats.prY} r="3" className="fill-sky-500 animate-pulse" filter="url(#glow)" />}
                </g>
                
                {/* Floating Center Badge */}
                {isFloating && (
                    <circle cx={CENTER} cy={CENTER} r="2" className={`${theme.mode === 'dark' ? 'fill-slate-900 stroke-slate-700' : 'fill-white stroke-slate-200'} stroke-2 drop-shadow-md`} />
                )}
            </svg>

            {/* 2. The Anchors (Icons) - DIAGONAL POSITIONING */}
            
            {/* Top Left: PASSION (Rose) */}
            <div className="absolute flex flex-col items-center justify-center -translate-x-1/2 -translate-y-1/2 transition-all duration-500"
                 style={{ top: toPct(CENTER - ANCHOR_OFFSET), left: toPct(CENTER - ANCHOR_OFFSET) }}>
                <div className={`p-2 rounded-full border shadow-lg transition-all duration-500 z-10 ${stats.s.p > 0 ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800' : 'bg-white dark:bg-slate-900 border-white/20 dark:border-white/10'}`}>
                    <Heart size={16} className={stats.s.p > 0 ? "text-rose-500 fill-rose-500" : "text-rose-500 opacity-60"} />
                </div>
            </div>

            {/* Top Right: MISSION (Purple) */}
            <div className="absolute flex flex-col items-center justify-center -translate-x-1/2 -translate-y-1/2 transition-all duration-500"
                 style={{ top: toPct(CENTER - ANCHOR_OFFSET), left: toPct(CENTER + ANCHOR_OFFSET) }}>
                <div className={`p-2 rounded-full border shadow-lg transition-all duration-500 z-10 ${stats.s.m > 0 ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' : 'bg-white dark:bg-slate-900 border-white/20 dark:border-white/10'}`}>
                    <Globe size={16} className={stats.s.m > 0 ? "text-purple-500" : "text-purple-500 opacity-60"} />
                </div>
            </div>

            {/* Bottom Right: VOCATION (Emerald) */}
            <div className="absolute flex flex-col items-center justify-center -translate-x-1/2 -translate-y-1/2 transition-all duration-500"
                 style={{ top: toPct(CENTER + ANCHOR_OFFSET), left: toPct(CENTER + ANCHOR_OFFSET) }}>
                <div className={`p-2 rounded-full border shadow-lg transition-all duration-500 z-10 ${stats.s.v > 0 ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'bg-white dark:bg-slate-900 border-white/20 dark:border-white/10'}`}>
                    <Briefcase size={16} className={stats.s.v > 0 ? "text-emerald-500" : "text-emerald-500 opacity-60"} />
                </div>
            </div>

            {/* Bottom Left: PROFESSION (Sky) */}
            <div className="absolute flex flex-col items-center justify-center -translate-x-1/2 -translate-y-1/2 transition-all duration-500"
                 style={{ top: toPct(CENTER + ANCHOR_OFFSET), left: toPct(CENTER - ANCHOR_OFFSET) }}>
                <div className={`p-2 rounded-full border shadow-lg transition-all duration-500 z-10 ${stats.s.pr > 0 ? 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800' : 'bg-white dark:bg-slate-900 border-white/20 dark:border-white/10'}`}>
                    <Zap size={16} className={stats.s.pr > 0 ? "text-sky-500 fill-sky-500" : "text-sky-500 opacity-60"} />
                </div>
            </div>

            {/* Floating Center Icon */}
            {isFloating && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                    <Radar size={14} className={`${theme.mode === 'dark' ? 'text-indigo-400' : 'text-indigo-500'} opacity-80 animate-spin-slow`} />
                </div>
            )}

         </div>
      </div>

      {/* --- FOOTER (Status) --- */}
      {/* In floating mode, this status is rendered outside or minimized */}
      {!isFloating && (
        <div className="z-20 text-center w-full mt-[-10px] pb-2">
            <h3 className={`text-xl font-black uppercase tracking-tighter leading-none transition-colors duration-500 ${activeTheme.text}`}>
                {stats.label}
            </h3>
            <p className={`text-[10px] font-bold uppercase tracking-widest opacity-50 mt-1 ${theme.colors.text.secondary}`}>
                {stats.subLabel}
            </p>
        </div>
      )}

      {/* Floating Status Label (Only when floating) */}
      {isFloating && (
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-center pointer-events-none backdrop-blur-md px-3 py-1 rounded-full bg-white/50 dark:bg-slate-900/50 border border-white/20 shadow-sm">
              <span className={`text-[10px] font-black uppercase tracking-widest ${activeTheme.text}`}>
                  {stats.label}
              </span>
          </div>
      )}

      {/* Background Decor */}
      {!isFloating && <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-white/10 pointer-events-none"></div>}
    </div>
  );
};
